import { useEffect, useState } from 'preact/hooks';
import { GlanceHeader } from '../components/GlanceHeader';
import { MediaImage } from '../components/MediaEl';
import { listMedia, selectMedia, getResume, type ActorMedia, type Resume } from '../lib/api';
import { mediaLabel, mbool } from './materialsPresentation';
import type { MaterialsSection } from '../routes/route';

export interface MaterialsMeProps {
  tenantId: string;
  userName: string;
  onNavigate: (section: MaterialsSection) => void;
}

// B5b — THIS IS ME: the identity glance. The name comes off the real session
// (same as Today's glance; no union/age is invented when no profile record
// backs it). Below it, the selectable "looks" — tapping a headshot toggles its
// is_selected flag (MULTI: several looks can represent you at once). Then two
// glance cards linking deeper: the auto-built resume (credit count) and the
// current reel.
export function MaterialsMe({ tenantId, userName, onNavigate }: MaterialsMeProps) {
  const [headshots, setHeadshots] = useState<ActorMedia[] | null>(null);
  const [reels, setReels] = useState<ActorMedia[] | null>(null);
  const [resume, setResume] = useState<Resume | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      listMedia(tenantId, 'headshot').catch(() => []),
      listMedia(tenantId, 'reel').catch(() => []),
      getResume(tenantId).catch(() => null),
    ]).then(([h, r, res]) => {
      if (cancelled) return;
      setHeadshots(h);
      setReels(r);
      setResume(res);
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function toggleSelected(m: ActorMedia) {
    if (busy) return;
    setBusy(m.id);
    const next = !mbool(m.fields, 'is_selected');
    try {
      await selectMedia(tenantId, m.id, { is_selected: next });
      setHeadshots((prev) =>
        (prev ?? []).map((x) => (x.id === m.id ? { ...x, fields: { ...x.fields, is_selected: next } } : x))
      );
    } catch {
      /* leave the flag as it was — a failed toggle is calm, not fatal */
    } finally {
      setBusy(null);
    }
  }

  const creditCount = resume ? resume.creditGroups.reduce((n, g) => n + g.credits.length, 0) : null;
  const currentReel = (reels ?? []).find((r) => mbool(r.fields, 'is_current')) ?? (reels ?? [])[0] ?? null;

  return (
    <div class="materials__scroll">
      <GlanceHeader name={userName} />

      <div class="materials__eyebrow">Pick what represents you now</div>
      {headshots === null ? (
        <div class="materials__empty">One moment…</div>
      ) : headshots.length === 0 ? (
        <div class="materials__empty">
          Nothing set yet. Pick what represents you once your headshots are in.
        </div>
      ) : (
        <div class="looks">
          {headshots.map((m) => {
            const on = mbool(m.fields, 'is_selected');
            return (
              <button
                key={m.id}
                type="button"
                class={`look${on ? ' look--on' : ''}`}
                aria-pressed={on}
                aria-label={`${mediaLabel(m)}${on ? ' — selected' : ''}`}
                disabled={busy === m.id}
                onClick={() => toggleSelected(m)}
              >
                <MediaImage tenantId={tenantId} id={m.id} alt={mediaLabel(m)} />
              </button>
            );
          })}
        </div>
      )}

      <button type="button" class="glancecard" onClick={() => onNavigate('resume')}>
        <div class="glancecard__row">
          <span class="glancecard__name">
            Resume{creditCount !== null ? ` · ${creditCount} credit${creditCount === 1 ? '' : 's'}` : ''}
          </span>
          <span class="glancecard__mini">auto</span>
        </div>
        <div class="glancecard__sub">built from your bookings · tailored exports · pick a headshot</div>
      </button>

      <button type="button" class="glancecard" onClick={() => onNavigate('reels')}>
        <div class="glancecard__row">
          <span class="glancecard__name">{currentReel ? mediaLabel(currentReel) : 'Demo reel'}</span>
          {currentReel && typeof currentReel.fields['duration'] === 'string' ? (
            <span class="glancecard__mini">{String(currentReel.fields['duration'])}</span>
          ) : null}
        </div>
        <div class="glancecard__sub">
          {currentReel ? 'current · older cuts in history' : 'no reel yet · add one on Reels'}
        </div>
      </button>
    </div>
  );
}
